import { createContext } from 'react';
import type React from 'react';
import type { ReasoningEffort, Verbosity } from '../../services/data.store';

// Per-thread chat settings, controlled via the AdvancedThreadControls panel.
export interface ThreadSettings {
  verbosity: Verbosity;
  model: string;
  reasoning: ReasoningEffort;
  // Store the model's reasoning summary with the thread's LLM usage (debugging)
  captureThinking: boolean;
}

// Provided by the Ellie/Ruthie wrappers around <Thread />. Undefined means the
// surface doesn't support verbosity control (e.g. EDA/onboarding flows) —
// render everything as usual.
export const ThreadSettingsContext = createContext<ThreadSettings | undefined>(undefined);

/**
 * Mark a tool call component as always visible, even when low verbosity hides
 * tool activity. Call this next to the component definition:
 *
 *   export const MyToolCall = ...;
 *   showInConciseMode(MyToolCall);
 *
 * Use for tools whose UI IS the result (charts, artifacts) or that require
 * user interaction (file uploads). Process/lookup tools should stay unmarked
 * so low verbosity hides them.
 */
export function showInConciseMode<T extends React.ComponentType<any>>(component: T): T {
  (component as any).__showInConciseMode = true;
  return component;
}

export function isVisibleInConciseMode(component?: React.ComponentType<any>): boolean {
  return !!component && (component as any).__showInConciseMode === true;
}
